import type { BrickKind } from './catalogue.js';
import { effectiveSize } from './catalogue.js';
import type { MaterialId, Mm, Rotation } from './units.js';
import { normaliseMaterialId, normaliseRotation, quantise } from './units.js';
import { fail } from './errors.js';

export type BrickId = number;

/**
 * The core is deliberately colour-blind (design document D1): a brick
 * references a material by id and the palette lives in `@bw/document`. This
 * is the id a brick takes when none is supplied.
 *
 * It matches the first entry of the document layer's palette on purpose: a
 * brick laid without an explicit material must still name a real one.
 */
export const DEFAULT_MATERIAL_ID: MaterialId = 'm1';

/**
 * A laid brick. IMMUTABLE by design (design document B3): recolouring or
 * moving replaces the object rather than mutating it, which is what makes an
 * undo snapshot an array of shared pointers instead of a deep clone.
 *
 * Derived extents are cached at construction. They cannot drift because the
 * record never changes, and the collision, snapping and culling hot paths read
 * them on every brick they touch.
 */
export interface PlacedBrick {
  readonly id: BrickId;
  readonly kind: BrickKind;
  /** Lower-left corner, millimetres, on the 0.125 mm lattice. */
  readonly x: Mm;
  readonly y: Mm;
  readonly rotation: Rotation;
  readonly materialId: MaterialId;
  readonly width: Mm;
  readonly height: Mm;
  readonly right: Mm;
  readonly top: Mm;
  readonly centreX: Mm;
}

export interface BrickInit {
  readonly id: BrickId;
  readonly kind: BrickKind;
  readonly x: number;
  readonly y: number;
  readonly rotation?: number;
  readonly materialId?: MaterialId;
}

export function assertBrickId(value: unknown): asserts value is BrickId {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    fail('Brick IDs must be positive integers');
  }
}

export function createBrick(init: BrickInit): PlacedBrick {
  assertBrickId(init.id);
  const rotation = normaliseRotation(init.rotation ?? 0);
  const materialId = normaliseMaterialId(init.materialId ?? DEFAULT_MATERIAL_ID);
  const [width, height] = effectiveSize(init.kind, rotation);
  const x = quantise(init.x);
  const y = quantise(init.y);
  return Object.freeze({
    id: init.id,
    kind: init.kind,
    x,
    y,
    rotation,
    materialId,
    width,
    height,
    right: x + width,
    top: y + height,
    centreX: x + width / 2,
  });
}

/** Return a copy translated by an offset, preserving id, kind and material. */
export function translateBrick(brick: PlacedBrick, dx: Mm, dy: Mm): PlacedBrick {
  return createBrick({
    id: brick.id,
    kind: brick.kind,
    x: brick.x + dx,
    y: brick.y + dy,
    rotation: brick.rotation,
    materialId: brick.materialId,
  });
}

/** Return a copy with a different material. */
export function withMaterial(brick: PlacedBrick, materialId: MaterialId): PlacedBrick {
  return createBrick({
    id: brick.id,
    kind: brick.kind,
    x: brick.x,
    y: brick.y,
    rotation: brick.rotation,
    materialId,
  });
}
